/* eslint-disable @typescript-eslint/strict-boolean-expressions */
import {
  Stack,
  Typography,
  Grid,
  Box,
  IconButton,
  Popper,
  Paper,
  ClickAwayListener,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useState } from 'react';
import {
  NEW_POSITION_ID,
  Position as PositionType,
  StrategyDefinition,
  StrategyType,
} from 'types/optionsCalculator';
import { RawGreeksDataMap } from 'types/impliedVol';
import { generateOptionLegsFromStrategy } from 'util/optionsCalculator';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import { OC_STRATEGIES } from 'config/optionsCalculator';
import { useOptionsCalculatorActions } from 'hooks/optionsCalculator/useOptionsCalculatorActions';
import { sleep } from 'util/shared/date';

interface StrategiesContainerProps {
  onUpdateNewPosition: (updates: Partial<PositionType>) => void;
  greeksData?: RawGreeksDataMap;
  currentPrice?: number;
  sym: string;
  setTab: (tab: string) => void;
  setBaseDate: (baseDate: number) => void;
  setForceDefaultZoom: (forceDefaultZoom: boolean) => void;
  dividendDates?: Float64Array;
  dividendAmounts?: Float64Array;
}

const StrategiesContainer = ({
  onUpdateNewPosition,
  greeksData,
  currentPrice,
  sym,
  setTab,
  setBaseDate,
  setForceDefaultZoom,
  dividendDates = new Float64Array(0),
  dividendAmounts = new Float64Array(0),
}: StrategiesContainerProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [selectedStrategy, setSelectedStrategy] =
    useState<StrategyDefinition | null>(null);
  const [popperOpen, setPopperOpen] = useState(false);
  const [popperAnchor, setPopperAnchor] = useState<HTMLElement | null>(null);
  const [popperStrategy, setPopperStrategy] =
    useState<StrategyDefinition | null>(null);
  const { getRiskFreeRate } = useOptionsCalculatorActions();

  const handleStrategySelect = async (strategy: StrategyType) => {
    if (!onUpdateNewPosition || !greeksData || !currentPrice || !sym) {
      return;
    }

    // Use the generateOptionLegsFromStrategy function to create proper option legs
    const optionLegs = generateOptionLegsFromStrategy(
      strategy,
      greeksData,
      sym,
      currentPrice,
      getRiskFreeRate,
      dividendDates,
      dividendAmounts,
    );

    // Find the closest expiration date from the generated option legs
    if (optionLegs.length > 0) {
      const closestExpiry = Math.min(...optionLegs.map((leg) => leg.expiry));
      setBaseDate(closestExpiry);
    }
    // In canvas mode, symbols are set separately from the rest of the config
    // this causes a TOCTOU race between write of sym -> server and legs -> server
    // Normally this synchronous setting of state doesn't happen, except when
    // overhauling the entire strategy.
    //
    // This sleep 1ms propagates the legs to the next react work tick.
    await sleep(1);
    onUpdateNewPosition({
      id: NEW_POSITION_ID,
      option_legs: optionLegs,
      underlying_shares: [], // Reset shares when applying strategy
      iv_adjustments: [],
      sym,
    });

    setTab('new');
    setForceDefaultZoom(true);
  };

  const handleStrategyHover = (
    strategy: StrategyDefinition | null,
    element: HTMLElement | null,
  ) => {
    if (isMobile) {
      return;
    } // Don't show popper on hover for mobile

    if (strategy && element) {
      setPopperStrategy(strategy);
      setPopperAnchor(element);
      setPopperOpen(true);
    } else {
      setPopperOpen(false);
    }
  };

  const handleStrategyClick = (strategy: StrategyDefinition) => {
    setSelectedStrategy(strategy);
    void handleStrategySelect(strategy.id);
  };

  const handleInfoClick = (
    strategy: StrategyDefinition,
    element: HTMLElement,
    event: React.MouseEvent,
  ) => {
    event.stopPropagation();
    if (popperOpen && popperStrategy?.id === strategy.id) {
      setPopperOpen(false);
    } else {
      setPopperStrategy(strategy);
      setPopperAnchor(element);
      setPopperOpen(true);
    }
  };

  const handlePopperClose = () => {
    setPopperOpen(false);
  };

  // Group strategies by category
  const strategiesByCategory = OC_STRATEGIES.reduce(
    (acc, strategy) => {
      if (!acc[strategy.category]) {
        acc[strategy.category] = [];
      }
      acc[strategy.category].push(strategy);
      return acc;
    },
    {} as Record<string, StrategyDefinition[]>,
  );

  const StrategyDetails = ({ strategy }: { strategy: StrategyDefinition }) => (
    <Paper
      sx={{
        p: 4,
        maxWidth: isMobile ? '90vw' : 300,
        boxShadow: 1,
        borderColor: theme.palette.primary.main,
        borderWidth: 1,
        borderStyle: 'solid',
        borderRadius: 2,
        opacity: 1,
        color: theme.palette.primary.main,
      }}
    >
      <Stack sx={{ gap: 2 }}>
        <Typography variant="body2" color="text.secondary">
          {strategy.description}
        </Typography>

        <Box>
          <Typography variant="caption" color="text.secondary">
            Legs: {strategy.legs.length}
          </Typography>
        </Box>
      </Stack>
    </Paper>
  );

  return (
    <Stack spacing={3}>
      <Typography variant="h6" color="text.primary">
        Strategies
      </Typography>

      <Grid container spacing={2}>
        {Object.entries(strategiesByCategory).map(([category, strategies]) => (
          <Grid item xs={12} sm={6} md={4} key={category}>
            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mb: 0.5, fontWeight: 600, fontSize: '10px' }}
              >
                {category.toUpperCase()}
              </Typography>
              <Grid container spacing={2}>
                {strategies.map((strategy) => (
                  <Grid item xs={6} key={strategy.id}>
                    <Box
                      sx={{
                        p: 0.75,
                        borderRadius: 0.5,
                        cursor: 'pointer',
                        transition: 'all 0.2s ease-in-out',
                        bgcolor:
                          selectedStrategy?.id === strategy.id
                            ? 'action.selected'
                            : 'background.paper',
                        '&:hover': {
                          bgcolor: 'action.hover',
                          borderColor: 'primary.main',
                        },
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 0.5,
                      }}
                      onClick={() => handleStrategyClick(strategy)}
                      onMouseEnter={(e) =>
                        handleStrategyHover(strategy, e.currentTarget)
                      }
                      onMouseLeave={() => handleStrategyHover(null, null)}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          fontWeight: 500,
                          color: theme.palette.sgGreen,
                          fontSize: '13px',
                          lineHeight: 1.2,
                          flex: 1,
                          transition: 'all 0.2s ease-in-out',
                          textDecoration: 'underline',
                          position: 'relative',
                          '&:hover': {
                            color: theme.palette.text.primary,
                          },
                        }}
                      >
                        {strategy.name}
                      </Typography>
                      {isMobile && (
                        <IconButton
                          data-ph-capture-attribute-element="opt-calc-strategy-info"
                          size="small"
                          color="primary"
                          onClick={(e) =>
                            handleInfoClick(strategy, e.currentTarget, e)
                          }
                        >
                          <InfoOutlined sx={{ fontSize: 18 }} />
                        </IconButton>
                      )}
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Box>
          </Grid>
        ))}
      </Grid>

      <Popper
        open={popperOpen}
        anchorEl={popperAnchor}
        placement={isMobile ? 'bottom' : 'left-start'}
        sx={{ zIndex: theme.zIndex.tooltip }}
        modifiers={[
          {
            name: 'offset',
            options: {
              offset: [0, 8],
            },
          },
          {
            name: 'preventOverflow',
            enabled: true,
            options: {
              altAxis: true,
              altBoundary: true,
              tether: true,
              rootBoundary: 'viewport',
              padding: 8,
            },
          },
          {
            name: 'flip',
            enabled: true,
            options: {
              fallbackPlacements: isMobile
                ? [
                    'bottom',
                    'top',
                    'bottom-start',
                    'bottom-end',
                    'top-start',
                    'top-end',
                  ]
                : ['left-start', 'right-start', 'bottom', 'top'],
            },
          },
        ]}
      >
        <ClickAwayListener onClickAway={handlePopperClose}>
          <div>
            {popperStrategy && <StrategyDetails strategy={popperStrategy} />}
          </div>
        </ClickAwayListener>
      </Popper>
    </Stack>
  );
};

export default StrategiesContainer;
